import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, CheckCircle } from 'lucide-react';

// Props interface for the FancyLoader component
interface FancyLoaderProps {
    message?: string;
    size?: 'sm' | 'md' | 'lg';
    theme?: 'primary' | 'success' | 'warning' | 'danger';
    showBackground?: boolean;
    showProgressDots?: boolean;
    isSuccess?: boolean;
    onAnimationComplete?: () => void;
}

// Component that displays an elaborate loading animation
export const FancyLoader: React.FC<FancyLoaderProps> = ({
    message = 'Loading...',
    size = 'md',
    theme = 'primary',
    showBackground = true,
    showProgressDots = true,
    isSuccess = false,
    onAnimationComplete
}) => {
    // Size mappings for different elements
    const sizeMap = {
        sm: {
            container: 'max-w-xs',
            icon: 'w-8 h-8',
            text: 'text-sm',
            orbit: 'w-16 h-16'
        },
        md: {
            container: 'max-w-sm',
            icon: 'w-12 h-12',
            text: 'text-base',
            orbit: 'w-24 h-24'
        },
        lg: {
            container: 'max-w-md',
            icon: 'w-16 h-16',
            text: 'text-lg',
            orbit: 'w-32 h-32'
        }
    };

    // Theme color mappings
    const themeMap = {
        primary: {
            main: 'text-blue-500 dark:text-blue-400',
            secondary: 'text-blue-300 dark:text-blue-600',
            background: 'bg-blue-50 dark:bg-blue-900/20',
            border: 'border-blue-200 dark:border-blue-800',
            shadow: 'shadow-blue-500/20 dark:shadow-blue-400/10'
        },
        success: {
            main: 'text-green-500 dark:text-green-400',
            secondary: 'text-green-300 dark:text-green-600',
            background: 'bg-green-50 dark:bg-green-900/20',
            border: 'border-green-200 dark:border-green-800',
            shadow: 'shadow-green-500/20 dark:shadow-green-400/10'
        },
        warning: {
            main: 'text-amber-500 dark:text-amber-400',
            secondary: 'text-amber-300 dark:text-amber-600',
            background: 'bg-amber-50 dark:bg-amber-900/20',
            border: 'border-amber-200 dark:border-amber-800',
            shadow: 'shadow-amber-500/20 dark:shadow-amber-400/10'
        },
        danger: {
            main: 'text-red-500 dark:text-red-400',
            secondary: 'text-red-300 dark:text-red-600',
            background: 'bg-red-50 dark:bg-red-900/20',
            border: 'border-red-200 dark:border-red-800',
            shadow: 'shadow-red-500/20 dark:shadow-red-400/10'
        }
    };

    // Tailwind classes for different animations
    const pulseAnimation = 'animate-pulse';
    const spinAnimation = 'animate-spin';

    // Progress dots animation variants
    const dotsVariants = {
        animate: {
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    // Single dot animation variants
    const dotVariants = {
        initial: {
            opacity: 0,
            y: 0
        },
        animate: {
            opacity: [0, 1, 0],
            y: [0, -5, 0],
            transition: {
                duration: 1.5,
                repeat: Infinity
            }
        }
    };

    // Orbit animation variants
    const orbitVariants = {
        animate: {
            rotate: 360,
            transition: {
                duration: 8,
                repeat: Infinity,
                ease: "linear"
            }
        }
    };

    // Success animation variants
    const successVariants = {
        initial: {
            scale: 0.8,
            opacity: 0
        },
        animate: {
            scale: 1,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 200,
                damping: 10
            }
        }
    };

    return (
        <div className={`relative flex flex-col items-center justify-center py-10 ${sizeMap[size].container}`}>
            {/* Background container with pulse effect */}
            {showBackground && (
                <div
                    className={`absolute inset-0 rounded-xl ${themeMap[theme].background} ${themeMap[theme].border} border ${isSuccess ? '' : pulseAnimation} ${themeMap[theme].shadow} shadow-lg opacity-70`}
                />
            )}

            {/* Main content */}
            <div className="relative z-10 flex flex-col items-center justify-center py-8 px-6">
                {!isSuccess && (
                    <>
                        {/* Orbit element */}
                        <motion.div
                            className={`absolute ${sizeMap[size].orbit} rounded-full border-2 border-dashed ${themeMap[theme].border} opacity-60`}
                            variants={orbitVariants}
                            animate="animate"
                        >
                            {/* Orbiting dot */}
                            <motion.div
                                className={`absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full ${themeMap[theme].main}`}
                            />
                        </motion.div>

                        {/* Main loader icon */}
                        <div className="relative">
                            <Loader2
                                className={`${sizeMap[size].icon} ${themeMap[theme].main} ${spinAnimation}`}
                            />

                            {/* Decorative glow */}
                            <div className={`absolute inset-0 ${themeMap[theme].main} blur-md opacity-20 animate-pulse`} />
                        </div>
                    </>
                )}

                {isSuccess && (
                    <motion.div
                        variants={successVariants}
                        initial="initial"
                        animate="animate"
                        className="relative"
                    >
                        <CheckCircle
                            className={`${sizeMap[size].icon} ${themeMap.success.main}`}
                        />

                        {/* Success glow */}
                        <div className={`absolute inset-0 ${themeMap.success.main} blur-md opacity-20`} />
                    </motion.div>
                )}

                {/* Message text */}
                <p className={`${sizeMap[size].text} font-medium text-center mt-4 text-gray-700 dark:text-gray-200`}>
                    {message}
                </p>

                {/* Progress dots */}
                {showProgressDots && !isSuccess && (
                    <motion.div
                        className="flex space-x-2 mt-3"
                        variants={dotsVariants}
                        initial="initial"
                        animate="animate"
                    >
                        {[0, 1, 2].map((i) => (
                            <motion.div
                                key={i}
                                className={`w-2 h-2 rounded-full ${themeMap[theme].secondary}`}
                                variants={dotVariants}
                            />
                        ))}
                    </motion.div>
                )}
            </div>
        </div>
    );
};

// LoaderWrapper component that adds different mounting/unmounting animations
export const LoaderWrapper: React.FC<{
    children: React.ReactNode;
    isLoading: boolean;
}> = ({ children, isLoading }) => {
    // Animation variants for the loader appearance/disappearance
    const containerVariants = {
        hidden: {
            opacity: 0,
            scale: 0.9,
            y: 20
        },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                duration: 0.4,
                ease: "easeOut"
            }
        },
        exit: {
            opacity: 0,
            scale: 0.9,
            y: -20,
            transition: {
                duration: 0.3,
                ease: "easeIn"
            }
        }
    };

    return (
        <div className="relative w-full">
            <AnimatePresence>
                {isLoading ? (
                    <motion.div
                        key="loader"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="absolute inset-0 flex items-center justify-center"
                    >
                        {children}
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </div>
    );
};

export default FancyLoader;